import React, { useContext, useState, useEffect, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { AuthContext } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { 
  AlertDialog, 
  AlertDialogAction, 
  AlertDialogContent, 
  AlertDialogDescription, 
  AlertDialogFooter, 
  AlertDialogHeader, 
  AlertDialogTitle 
} from "@/components/ui/alert-dialog";
import { Trophy, ArrowLeft, Save, Plus, Minus, AlertCircle, Calendar as CalendarIcon } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { formatDateUK, getLocalTodayStr, parseLocalDate } from "@/utils/dateUtils";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { cn } from "@/lib/utils";
import Logo from "@/components/Logo";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function PlayerEnterScore() {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  const { id } = useParams();
  const [competition, setCompetition] = useState(null);
  const [course, setCourse] = useState(null);
  const [bestScores, setBestScores] = useState(null);
  const [isFirstRound, setIsFirstRound] = useState(false);
  const [loading, setLoading] = useState(true);
  const [roundDate, setRoundDate] = useState(''); // Empty default - force user to select
  const [courseHandicap, setCourseHandicap] = useState('');
  const [entryMode, setEntryMode] = useState("full"); // "full" or "selective"
  const [scores, setScores] = useState([]);
  const [inputValues, setInputValues] = useState({}); // Track input values for validation display
  const [existingRoundDates, setExistingRoundDates] = useState([]); // Dates where player has rounds
  const [submissionSuccess, setSubmissionSuccess] = useState(false); // Track successful submission
  const [currentRoundsCount, setCurrentRoundsCount] = useState(0); // Track player's current rounds
  const [errorDialog, setErrorDialog] = useState({ open: false, title: "", message: "" }); // Error dialog state
  const [editingHole, setEditingHole] = useState(null);   // Hole currently being edited (subsequent-round flow)
  const [dirtyHoles, setDirtyHoles] = useState(new Set());// Holes user has confirmed as improvements
  const [activeHoleIdx, setActiveHoleIdx] = useState(0);  // Round 1 carousel position (0-based within scores[])
  const [reviewing, setReviewing] = useState(false);      // Review & submit terminal step
  const [submitting, setSubmitting] = useState(false);    // UI: disables the button visually
  const submitInFlight = useRef(false);                   // Race guard — set true only once axios starts

  // Helper function to get hole range based on competition format
  const getHoleRange = (holeFormat) => {
    if (holeFormat === "front_9") {
      return { start: 1, end: 9, total: 9 };
    } else if (holeFormat === "back_9") {
      return { start: 10, end: 18, total: 9 };
    } else {
      return { start: 1, end: 18, total: 18 };
    }
  };

  useEffect(() => {
    if (user) {
      fetchData();
    }
  }, [user, id]);

  const fetchData = async () => {
    try {
      const [compResp, roundsResp, bestResp] = await Promise.all([
        axios.get(`${API}/competitions`, { withCredentials: true }),
        axios.get(`${API}/competitions/${id}/my-rounds`, { withCredentials: true }),
        axios.get(`${API}/competitions/${id}/my-best`, { withCredentials: true }).catch(() => null)
      ]);
      
      const comp = compResp.data.find(c => c.id === id);
      setCompetition(comp);

      // Default the date picker to TODAY (clamped to the competition's date
      // range). Long comps (e.g. Jun–Aug summer eclectic) previously opened
      // on the start month, forcing users to navigate months forward on
      // every submit — a common complaint in Feb 2026 production.
      if (comp) {
        const today = getLocalTodayStr();
        let defaultDate = today;
        if (comp.start_date && today < comp.start_date) defaultDate = comp.start_date;
        else if (comp.end_date && today > comp.end_date) defaultDate = comp.end_date;
        setRoundDate((prev) => prev || defaultDate);
      }

      // Fetch course (for par-aware UI)
      if (comp?.course_id) {
        try {
          const cr = await axios.get(`${API}/courses/${comp.course_id}`, { withCredentials: true });
          setCourse(cr.data);
        } catch (e) {
          setCourse(null);
        }
      }
      
      // Get hole range based on competition format
      const holeFormat = comp?.hole_format || "18";
      const { start, end, total } = getHoleRange(holeFormat);
      
      // Extract dates from existing rounds and sort them chronologically (newest first)
      const roundDates = roundsResp.data
        .map(r => r.round_date)
        .sort((a, b) => new Date(b) - new Date(a));
      setExistingRoundDates(roundDates);
      
      // Set current rounds count
      setCurrentRoundsCount(roundsResp.data.length);
      
      // Check if this is the first round
      const isFirst = roundsResp.data.length === 0;
      setIsFirstRound(isFirst);
      
      // LOGIC: Round 1 = zeros, Round 2+ = pre-populate with best scores
      if (!isFirst && bestResp?.data) {
        setBestScores(bestResp.data);
        setEntryMode("full"); // Use full mode but pre-populate with best scores
        
        // Pre-populate scores with current best scores for the relevant holes
        const initialScores = Array.from({ length: total }, (_, i) => {
          const holeNum = start + i;
          const bestHole = bestResp.data.hole_scores?.find(h => h.hole === holeNum);
          return {
            hole: holeNum,
            strokes: bestHole ? bestHole.strokes : 0
          };
        });
        setScores(initialScores);
      } else {
        setEntryMode("full"); // First round - start with zeros
        setScores(Array.from({ length: total }, (_, i) => ({ hole: start + i, strokes: 0 })));
      }
    } catch (error) {
      toast.error("Failed to load competition");
    } finally {
      setLoading(false);
    }
  };

  // ---- Par helpers (par-aware UI) ----
  const getTee = () => {
    if (!course) return null;
    const teeList = course.tees || [];
    return (competition?.tee_id && teeList.find(t => t.id === competition.tee_id)) || teeList[0] || null;
  };
  const getPar = (holeNum) => {
    const tee = getTee();
    if (!tee) return null;
    const h = (tee.holes || []).find(x => x.hole === holeNum);
    return h?.par ?? null;
  };
  const totalPar = () => {
    const tee = getTee();
    if (!tee) return null;
    const holeFormat = competition?.hole_format || "18";
    const { start, end } = getHoleRange(holeFormat);
    const sum = (tee.holes || [])
      .filter(h => h.hole >= start && h.hole <= end)
      .reduce((s, h) => s + (parseInt(h.par) || 0), 0);
    return sum || null;
  };

  const showErrorDialog = (title, message) => {
    console.log("🚨 ERROR DIALOG:", title, message); // DEBUG
    setErrorDialog({ open: true, title, message });
    // Scroll to top to make dialog more visible
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleScoreChange = (holeIndex, value) => {
    const newScores = [...scores];
    newScores[holeIndex].strokes = parseInt(value) || 0;
    setScores(newScores);
  };

  const handleSelectiveScoreChange = (holeNum, value) => {
    // Store input value for display
    setInputValues(prev => ({...prev, [holeNum]: value}));
    
    const newScores = [...scores.filter(s => s.hole !== holeNum)];
    
    // Only add if value is provided and valid
    if (value && parseInt(value) > 0) {
      const enteredScore = parseInt(value);
      const bestScore = getBestScoreForHole(holeNum);
      
      // In selective mode, only accept improved (lower) scores
      if (bestScore !== null && enteredScore >= bestScore) {
        // Don't add to scores array if not an improvement
        // The visual feedback will show this is not valid
        setScores(newScores);
        return;
      }
      
      newScores.push({ hole: holeNum, strokes: enteredScore });
    }
    
    setScores(newScores);
  };

  const getSelectiveScoreValue = (holeNum) => {
    return inputValues[holeNum] || '';
  };

  const isScoreImprovement = (holeNum, value) => {
    if (!value || parseInt(value) <= 0) return true; // Empty is ok
    const enteredScore = parseInt(value);
    const bestScore = getBestScoreForHole(holeNum);
    if (bestScore === null) return true; // No best score yet
    return enteredScore < bestScore; // Must be lower to be improvement
  };

  const getBestScoreForHole = (holeNum) => {
    if (!bestScores || !bestScores.hole_scores) return null;
    const holeScore = bestScores.hole_scores.find(hs => hs.hole === holeNum);
    return holeScore ? holeScore.strokes : null;
  };

  // Increment score for a hole (Full mode)
  const incrementScore = (holeIndex) => {
    const newScores = [...scores];
    if (newScores[holeIndex].strokes < 10) {
      newScores[holeIndex].strokes += 1;
      setScores(newScores);
    }
  };

  // Decrement score for a hole (Full mode)
  const decrementScore = (holeIndex) => {
    const newScores = [...scores];
    if (newScores[holeIndex].strokes > 0) {
      newScores[holeIndex].strokes -= 1;
      setScores(newScores);
    }
  };

  // Increment score for a hole (Selective mode)
  const incrementSelectiveScore = (holeNum) => {
    const currentValue = parseInt(inputValues[holeNum]) || 0;
    if (currentValue < 10) {
      const newValue = currentValue + 1;
      setInputValues(prev => ({...prev, [holeNum]: newValue.toString()}));
      handleSelectiveScoreChange(holeNum, newValue.toString());
    }
  };

  // Decrement score for a hole (Selective mode)
  const decrementSelectiveScore = (holeNum) => {
    const currentValue = parseInt(inputValues[holeNum]) || 0;
    if (currentValue > 0) {
      const newValue = currentValue - 1;
      setInputValues(prev => ({...prev, [holeNum]: newValue.toString()}));
      handleSelectiveScoreChange(holeNum, newValue.toString());
    }
  };

  const getEnteredScoresCount = () => {
    if (entryMode === "full") {
      const holeFormat = competition?.hole_format || "18";
      const { total } = getHoleRange(holeFormat);
      return total;
    } else {
      // In selective mode, scores array only contains holes with entered values
      return scores.length;
    }
  };

  // Round-date popover open state. Driven by the shadcn Popover wrapper so
  // the calendar closes as soon as the user picks a day (matches the rest of
  // the app's modal UX rather than the native browser date picker).
  const [datePopoverOpen, setDatePopoverOpen] = useState(false);


  const handleSubmit = async (e) => {
    e.preventDefault();
    // Guard against double-submit (mobile fat-finger + slow network combined
    // to create duplicate rounds on the same date in production Feb 2026).
    // The backend also has a unique index as a safety net.
    if (submitInFlight.current) return;
    console.log("🎯 SUBMIT CLICKED - roundDate:", roundDate, "handicap:", courseHandicap); // DEBUG
    
    // Validate date FIRST - must not be blank
    if (!roundDate) {
      console.log("❌ Date is blank"); // DEBUG
      showErrorDialog(
        "Round Date Required",
        "Please select a Round Date before submitting your scores. The date must be within the competition period."
      );
      return;
    }
    
    // Validate date is within competition range
    if (competition) {
      const selectedDate = parseLocalDate(roundDate);
      const startDate = parseLocalDate(competition.start_date);
      const endDate = parseLocalDate(competition.end_date);
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      
      if (selectedDate < startDate || selectedDate > endDate) {
        showErrorDialog(
          "Invalid Date Range",
          `The round date must be between ${formatDateUK(competition.start_date)} and ${formatDateUK(competition.end_date)}.`
        );
        return;
      }
      
      if (selectedDate > today) {
        showErrorDialog(
          "Future Date Not Allowed",
          "You cannot enter a round for a future date. Please select today or an earlier date."
        );
        return;
      }
    }
    
    // Validate handicap
    if (courseHandicap === '' || parseInt(courseHandicap) < 0 || parseInt(courseHandicap) > 54) {
      showErrorDialog(
        "Invalid Course Handicap",
        "Please enter a valid Course Handicap between 0 and 54."
      );
      return;
    }
    
    let scoresToSubmit = [];
    
    if (entryMode === "full") {
      // Check for zeros in full mode
      const zeroScores = scores.filter(s => s.strokes === 0);
      if (zeroScores.length > 0) {
        showErrorDialog(
          "Incomplete Scores",
          `Please enter scores for all holes. ${zeroScores.length} hole(s) still have 0.`
        );
        return;
      }
      
      // Validate all holes
      const invalidScores = scores.filter(s => s.strokes <= 0 || s.strokes > 10);
      if (invalidScores.length > 0) {
        showErrorDialog(
          "Invalid Scores",
          "Please enter valid scores (1-10) for all holes."
        );
        return;
      }
      
      // Check if any scores are improvements
      const improvements = scores.filter(s => {
        const bestScore = getBestScoreForHole(s.hole);
        return bestScore === null || s.strokes < bestScore;
      });
      
      // Confirm if no improvements in full round
      if (improvements.length === 0) {
        const confirmed = window.confirm(
          "⚠️ No Score Improvements Detected\n\n" +
          "You've entered a full round but none of the scores improve your current best scores.\n\n" +
          "This round will be recorded but won't update your best scores.\n\n" +
          "Are you sure you want to submit this round?"
        );
        if (!confirmed) {
          return; // User cancelled
        }
      }
      
      scoresToSubmit = scores;
    } else {
      // Selective mode - can submit 0 or more holes
      // Check if any scores are improvements (lower than current best)
      
      const improvements = scores.filter(s => {
        const bestScore = getBestScoreForHole(s.hole);
        // Improvement if: no previous best OR current score is lower than best
        return bestScore === null || (s.strokes > 0 && s.strokes < bestScore);
      });
      
      // Confirm if no improvements (to prevent accidental submissions)
      if (improvements.length === 0) {
        const confirmed = window.confirm(
          "⚠️ No Score Improvements Detected\n\n" +
          "You haven't improved your best score on any hole in this round.\n\n" +
          "This round will be recorded but won't update your best scores.\n\n" +
          "Are you sure you want to submit this round?\n\n" +
          "(This is especially important on mobile - tap 'Cancel' if you haven't finished entering scores)"
        );
        if (!confirmed) {
          return; // User cancelled
        }
      }
      
      // Validate entered scores (if any)
      const invalidScores = scores.filter(s => s.strokes <= 0 || s.strokes > 10);
      if (invalidScores.length > 0) {
        showErrorDialog(
          "Invalid Scores",
          "Please enter valid scores (1-10) for the holes you're submitting."
        );
        return;
      }
      
      scoresToSubmit = scores;
    }

    // Check for NEWLY ACHIEVED hole-in-one BEFORE submitting (not if they already had a hole-in-one)
    const newHolesInOne = scoresToSubmit.filter(s => {
      if (s.strokes !== 1) return false; // Not a hole-in-one
      // Check if this is a NEW hole-in-one (better than previous best)
      const bestScore = bestScores?.hole_scores?.find(h => h.hole === s.hole);
      return !bestScore || bestScore.strokes > 1; // New if no previous best OR previous best was > 1
    });
    
    if (newHolesInOne.length > 0) {
      const holeNumbers = newHolesInOne.map(h => h.hole).join(', ');
      const confirmed = window.confirm(
        `🎉 Congratulations! You've entered a score of 1 on hole(s): ${holeNumbers}\n\n` +
        `Is this a HOLE IN ONE?\n\n` +
        `Click OK to confirm, or Cancel to review your scores.`
      );
      if (!confirmed) {
        return; // User wants to review scores
      }
    }

    // Check for NEWLY ACHIEVED score of 2 (if competition has this feature enabled)
    const newScoresOfTwo = competition?.recognize_score_of_2 ? scoresToSubmit.filter(s => {
      if (s.strokes !== 2) return false; // Not a score of 2
      // Check if this is a NEW score of 2 (better than previous best)
      const bestScore = bestScores?.hole_scores?.find(h => h.hole === s.hole);
      return !bestScore || bestScore.strokes > 2; // New if no previous best OR previous best was > 2
    }) : [];
    
    if (newScoresOfTwo.length > 0) {
      const holeNumbers = newScoresOfTwo.map(h => h.hole).join(', ');
      const confirmed = window.confirm(
        `💎 Excellent! You've entered a score of 2 on hole(s): ${holeNumbers}\n\n` +
        `This is an EXCELLENT 2 achievement!\n\n` +
        `Click OK to confirm and post celebration, or Cancel to review your scores.`
      );
      if (!confirmed) {
        return; // User wants to review scores
      }
    }

    try {
      // Flip the in-flight guard and disable the button. The ref catches
      // rapid taps; the state drives UI feedback.
      submitInFlight.current = true;
      setSubmitting(true);
      // Submit the round
      await axios.post(`${API}/competitions/${id}/rounds`, {
        round_date: roundDate,
        course_handicap: parseInt(courseHandicap),
        scores: scoresToSubmit
      }, { withCredentials: true });
      
      // If there was a NEW hole-in-one, post celebration message
      if (newHolesInOne.length > 0) {
        try {
          const holeNumbers = newHolesInOne.map(h => h.hole);
          await axios.post(`${API}/competitions/${id}/celebrate-hole-in-one`, {
            holes: holeNumbers,
            date: roundDate
          }, { withCredentials: true });
          toast.success("🎉 Round submitted! Hole-in-one celebration posted!");
        } catch (msgError) {
          console.error("Failed to post celebration message:", msgError);
          toast.warning("Round submitted, but celebration message failed to post");
        }
      } else if (newScoresOfTwo.length > 0) {
        // If there was a NEW score of 2, post celebration message
        try {
          const holeNumbers = newScoresOfTwo.map(h => h.hole);
          await axios.post(`${API}/competitions/${id}/celebrate-score-of-2`, {
            holes: holeNumbers,
            date: roundDate
          }, { withCredentials: true });
          toast.success("💎 Round submitted! Excellent 2 celebration posted!");
        } catch (msgError) {
          console.error("Failed to post celebration message:", msgError);
          toast.warning("Round submitted, but celebration message failed to post");
        }
      } else {
        toast.success("Round scores submitted successfully");
      }
      setCurrentRoundsCount(currentRoundsCount + 1); // Increment rounds count
      setSubmissionSuccess(true); // Show success state with options
    } catch (error) {
      const errorMessage = error.response?.data?.detail || "Failed to submit scores. Please try again.";
      showErrorDialog(
        "Submission Failed",
        errorMessage
      );
    } finally {
      submitInFlight.current = false;
      setSubmitting(false);
    }
  };

  const handleEnterAnotherRound = () => {
    // Reset form for another round. Date defaults to today again (clamped
    // to the comp range inside fetchData below).
    setSubmissionSuccess(false);
    setRoundDate('');
    setCourseHandicap('');
    setScores(Array.from({ length: 18 }, (_, i) => ({ hole: i + 1, strokes: 0 })));
    setInputValues({});
    setDirtyHoles(new Set());
    setEditingHole(null);
    fetchData();
  };

  if (!user || loading || !competition) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-lg text-ink">Loading...</div>
      </div>
    );
  }

  // Success state after submission
  if (submissionSuccess) {
    return (
      <div className="min-h-screen bg-app">
        <header className="bg-white border-b border-line py-4 px-4 sm:py-6 sm:px-8 shadow-sm">
          <div className="container mx-auto flex justify-between items-center">
            <div className="flex items-center space-x-2 sm:space-x-3">
              <Logo variant="icon" size={36} />
              <h1 className="text-lg sm:text-xl font-semibold text-ink tracking-tight">Golf Eclectic</h1>
            </div>
          </div>
        </header>

        <main className="container mx-auto px-4 py-6 sm:px-8 sm:py-12 max-w-3xl">
          <Card className="border border-accent">
            <CardContent className="pt-8 pb-8 text-center">
              <div className="w-20 h-20 bg-accent-tint rounded-full flex items-center justify-center mx-auto mb-6">
                <Trophy className="w-12 h-12 text-accent" />
              </div>
              <h2 className="text-xl font-semibold text-ink tracking-tight mb-4">Round Submitted Successfully!</h2>
              <p className="text-lg text-ink-muted mb-8">Your scores have been recorded for {competition.name}</p>
              
              <div className="flex flex-col sm:flex-row gap-3 justify-center">
                <div className="flex flex-col items-center">
                  <Button
                    onClick={handleEnterAnotherRound}
                    disabled={currentRoundsCount >= competition.total_rounds}
                    className="bg-ink hover:bg-ink-hover flex items-center justify-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <span>➕ Enter Another Round</span>
                  </Button>
                  {currentRoundsCount >= competition.total_rounds && (
                    <p className="text-xs text-ink-soft mt-2">
                      You have completed all {competition.total_rounds} rounds
                    </p>
                  )}
                </div>
                <Button
                  onClick={() => navigate(`/player/competitions/${id}/summary`)}
                  variant="outline"
                  className="flex items-center justify-center space-x-2"
                >
                  <span>View Best Scores</span>
                </Button>
                <Button
                  onClick={() => navigate("/dashboard")}
                  variant="outline"
                  className="flex items-center justify-center space-x-2"
                >
                  <ArrowLeft className="w-4 h-4" />
                  <span>Back to Competitions</span>
                </Button>
              </div>
            </CardContent>
          </Card>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-app">
      {/* Header */}
      <header className="bg-white border-b border-line py-4 px-4 sm:py-6 sm:px-8 shadow-sm">
        <div className="container mx-auto flex justify-between items-center">
          <div className="flex items-center space-x-2 sm:space-x-3">
            <Logo variant="icon" size={36} />
            <h1 className="text-lg sm:text-xl font-semibold text-ink tracking-tight">Golf Eclectic</h1>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              onClick={() => navigate("/dashboard")}
              className="flex items-center space-x-1 sm:space-x-2 text-sm sm:text-base"
              size="sm"
            >
              <ArrowLeft className="w-4 h-4" />
              <span className="hidden sm:inline">Back</span>
            </Button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="container mx-auto px-4 py-6 sm:px-8 sm:py-12 max-w-5xl">
        <div className="mb-6 sm:mb-8">
          <h2 className="text-2xl sm:text-3xl font-semibold text-ink tracking-tight mb-2">{competition.name}</h2>
          <p className="text-base sm:text-lg text-ink-muted">Enter your round scores</p>
        </div>

        <form onSubmit={handleSubmit}>
          {/* Round Date and Handicap */}
          <Card className="mb-6">
            <CardHeader>
              <CardTitle>Round Details</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Date and Handicap on same line */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="round-date">Date Played</Label>
                  {/* Shadcn Popover + Calendar replaces the native HTML5 date
                      picker so the look matches the rest of the app on both
                      desktop and mobile. Selected dates round-trip via
                      YYYY-MM-DD strings using parseLocalDate/getLocalTodayStr
                      to avoid the timezone drift that bit us with new Date()
                      on the raw ISO string. */}
                  <Popover open={datePopoverOpen} onOpenChange={setDatePopoverOpen}>
                    <PopoverTrigger asChild>
                      <Button
                        id="round-date"
                        type="button"
                        variant="outline"
                        data-testid="round-date-input"
                        className={cn(
                          "w-full justify-start text-left font-normal mt-1.5",
                          !roundDate && "text-ink-muted"
                        )}
                      >
                        <CalendarIcon className="mr-2 h-4 w-4 text-ink-muted" />
                        {roundDate ? formatDateUK(roundDate) : "Select a date"}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent
                      className="w-auto p-0 bg-white"
                      align="start"
                      data-testid="round-date-popover"
                    >
                      <Calendar
                        mode="single"
                        weekStartsOn={1}
                        selected={roundDate ? parseLocalDate(roundDate) : undefined}
                        defaultMonth={roundDate ? parseLocalDate(roundDate) : parseLocalDate(getLocalTodayStr())}
                        onSelect={(d) => {
                          if (!d) return;
                          const y = d.getFullYear();
                          const m = String(d.getMonth() + 1).padStart(2, "0");
                          const day = String(d.getDate()).padStart(2, "0");
                          setRoundDate(`${y}-${m}-${day}`);
                          setDatePopoverOpen(false);
                        }}
                        disabled={(date) => {
                          // Block before competition start and after today.
                          const y = date.getFullYear();
                          const m = String(date.getMonth() + 1).padStart(2, "0");
                          const day = String(date.getDate()).padStart(2, "0");
                          const iso = `${y}-${m}-${day}`;
                          if (competition?.start_date && iso < competition.start_date) return true;
                          if (iso > getLocalTodayStr()) return true;
                          return false;
                        }}
                        modifiers={{
                          played: existingRoundDates.map((s) => parseLocalDate(s)),
                          compStart: competition?.start_date ? [parseLocalDate(competition.start_date)] : [],
                          compEnd: competition?.end_date ? [parseLocalDate(competition.end_date)] : [],
                        }}
                        modifiersClassNames={{
                          played: "bg-accent-tint text-accent-deep font-semibold",
                          compStart: "ring-1 ring-ink/30",
                          compEnd: "ring-1 ring-warm/40",
                        }}
                        initialFocus
                      />
                    </PopoverContent>
                  </Popover>
                </div>
                <div>
                  <Label htmlFor="course-handicap">Course Handicap</Label>
                  <Input
                    id="course-handicap"
                    data-testid="course-handicap-input"
                    type="number"
                    min="0"
                    max="54"
                    value={courseHandicap}
                    onChange={(e) => setCourseHandicap(e.target.value)}
                    required
                    placeholder="e.g., 12"
                  />
                </div>
              </div>
              
              {/* Info box below */}
              <div className="p-3 bg-app border border-line rounded-lg">
                <p className="text-[11px] font-semibold tracking-[0.08em] uppercase text-ink-muted mb-2">Competition &amp; round dates</p>
                <div className="space-y-1 text-[12.5px]">
                  <div className="flex items-center gap-2">
                    <span className="text-[10px] font-mono font-semibold uppercase tracking-wider px-1.5 py-0.5 rounded bg-surface text-ink-muted border border-line">Start</span>
                    <span className="text-ink-muted">
                      {competition?.start_date && new Date(competition.start_date).toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' })}
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-[10px] font-mono font-semibold uppercase tracking-wider px-1.5 py-0.5 rounded bg-surface text-ink-muted border border-line">End</span>
                    <span className="text-ink-muted">
                      {competition?.end_date && new Date(competition.end_date).toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' })}
                    </span>
                  </div>
                  {existingRoundDates.length > 0 && (
                    <div className="flex items-start gap-2 mt-2 pt-2 border-t border-line">
                      <span className="text-[10px] font-mono font-semibold uppercase tracking-wider px-1.5 py-0.5 rounded bg-accent-tint text-accent-deep border border-accent/20 mt-0.5">Played</span>
                      <div className="flex-1">
                        <div className="flex flex-wrap gap-1">
                          {existingRoundDates.map((date, idx) => (
                            <span key={idx} className="inline-block bg-surface border border-line text-ink-muted px-2 py-0.5 rounded text-[11.5px] font-mono tabular-nums">
                              {new Date(date).toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' })}
                            </span>
                          ))}
                        </div>
                        <p className="text-ink-soft text-[11px] mt-1.5">One round per day max</p>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>

          {/* ===== Score Entry — Variant B unified UI ===== */}
          {(() => {
            const holeFormat = competition?.hole_format || "18";
            const { start, end, total } = getHoleRange(holeFormat);
            const holeNumbers = Array.from({ length: end - start + 1 }, (_, i) => start + i);
            const detailsReady = roundDate && courseHandicap !== '';

            // Live counters
            const enteredCount = scores.filter(s => s.strokes > 0).length;
            const roundTotal = scores.reduce((sum, s) => sum + s.strokes, 0);
            const courseTotalPar = totalPar();
            const playedPar = scores.reduce((s, sc) => sc.strokes > 0 ? s + (getPar(sc.hole) || 0) : s, 0);
            const vsPar = enteredCount > 0 ? roundTotal - playedPar : 0;
            const vsParStr = enteredCount === 0 ? '—' : (vsPar === 0 ? 'E' : (vsPar > 0 ? `+${vsPar}` : `${vsPar}`));
            const vsParTone = enteredCount === 0 ? 'text-ink-soft' : (vsPar < 0 ? 'text-accent' : (vsPar > 0 ? 'text-ink-muted' : 'text-ink'));

            // Stepper helpers
            const setStrokes = (holeNum, val) => {
              const idx = scores.findIndex(s => s.hole === holeNum);
              if (idx < 0) return;
              const newScores = [...scores];
              newScores[idx] = { ...newScores[idx], strokes: Math.max(0, Math.min(10, val)) };
              setScores(newScores);
            };

            // Round 1 carousel state
            const r1ActiveIdx = Math.min(Math.max(0, activeHoleIdx), holeNumbers.length - 1);
            const r1ActiveHole = holeNumbers[r1ActiveIdx];
            const r1Score = scores[r1ActiveIdx]?.strokes || 0;
            const r1Par = getPar(r1ActiveHole);
            const r1Diff = r1Score === 0 ? null : (r1Par ? r1Score - r1Par : null);
            const r1DiffLabel = r1Score === 0 ? 'Tap + to enter your score'
              : r1Diff === null ? `Score ${r1Score}`
              : r1Diff === 0 ? 'Even'
              : r1Diff < 0 ? `${Math.abs(r1Diff)} under par`
              : `${r1Diff} over par`;
            const r1Tone = r1Score === 0 ? 'text-ink-soft'
              : r1Diff === null ? 'text-ink'
              : r1Diff < 0 ? 'text-accent'
              : r1Diff > 0 ? 'text-ink-soft'
              : 'text-ink';

            // ── REVIEW & SUBMIT (terminal step) ──
            if (reviewing) {
              const isFirstReview = isFirstRound;
              const dirtyList = Array.from(dirtyHoles).sort((a, b) => a - b);
              const strokesSaved = dirtyList.reduce((acc, holeNum) => {
                const idx = scores.findIndex(s => s.hole === holeNum);
                const best = bestScores?.hole_scores?.find(h => h.hole === holeNum)?.strokes || 0;
                const cur = idx >= 0 ? scores[idx].strokes : best;
                return acc + Math.max(0, best - cur);
              }, 0);

              return (
                <div data-testid="review-and-submit">
                  {!detailsReady && (
                    <div className="mb-4 px-4 py-3 bg-app border border-line rounded-xl flex items-start gap-3">
                      <div className="w-7 h-7 rounded-full bg-ink text-white flex items-center justify-center flex-shrink-0 mt-0.5">
                        <span className="font-mono text-sm font-semibold">!</span>
                      </div>
                      <div>
                        <p className="text-[13.5px] font-semibold text-ink">Round details required</p>
                        <p className="text-[12.5px] text-ink-muted mt-0.5">Enter the date played and your course handicap above before submitting.</p>
                      </div>
                    </div>
                  )}
                  <div className="mb-1 text-[11px] font-semibold tracking-[0.08em] uppercase text-ink-muted">Review &amp; submit</div>
                  <h2 className="text-2xl sm:text-3xl font-semibold tracking-tight text-ink mb-1">
                    {isFirstReview ? "Looks right?" : (dirtyList.length === 0 ? "No improvements this round" : `${dirtyList.length} improvement${dirtyList.length === 1 ? '' : 's'}`)}
                  </h2>
                  <p className="text-[14px] text-ink-muted mb-5">
                    {isFirstReview ? "Tap any hole to fix it before submitting." : (dirtyList.length === 0 ? "You can still submit to log this round, or go back and update any hole you beat." : "Tap any improved hole to revisit it.")}
                  </p>

                  <div
                    className="mb-5 flex flex-wrap items-center gap-x-4 gap-y-1.5 px-3.5 py-2.5 rounded-xl bg-app border border-line text-[12px] text-ink-muted"
                    data-testid="review-color-legend"
                  >
                    <span className="font-semibold uppercase tracking-[0.08em] text-[10.5px] text-ink-soft">Score key</span>
                    <span className="inline-flex items-center gap-1.5">
                      <span className="w-2 h-2 rounded-full bg-accent inline-block" />
                      <span className="text-ink">Under par</span>
                    </span>
                    <span className="inline-flex items-center gap-1.5">
                      <span className="w-2 h-2 rounded-full bg-ink inline-block" />
                      <span className="text-ink">At par</span>
                    </span>
                    <span className="inline-flex items-center gap-1.5">
                      <span className="w-2 h-2 rounded-full bg-ink-soft inline-block" />
                      <span className="text-ink">Over par</span>
                    </span>
                    {!isFirstReview && (
                      <span className="inline-flex items-center gap-1.5">
                        <span className="w-2 h-2 rounded-full bg-accent inline-block ring-2 ring-accent/30" />
                        <span className="text-ink">Improved</span>
                      </span>
                    )}
                  </div>
                  <div className="ge-stat-grid mb-5">
                    <div className="ge-stat">
                      <div className="ge-stat-label">Round total</div>
                      <div className="ge-stat-value">{roundTotal || 0}</div>
                    </div>
                    <div className="ge-stat">
                      <div className="ge-stat-label">{isFirstReview ? 'Course par' : 'Strokes saved'}</div>
                      <div className={`ge-stat-value ${!isFirstReview && strokesSaved > 0 ? 'text-accent' : ''}`}>
                        {isFirstReview ? (courseTotalPar ?? '—') : (strokesSaved || 0)}
                      </div>
                    </div>
                    <div className="ge-stat">
                      <div className="ge-stat-label">{isFirstReview ? 'Vs par' : 'Holes improved'}</div>
                      <div className={`ge-stat-value ${isFirstReview ? vsParTone : (dirtyList.length > 0 ? 'text-accent' : '')}`}>
                        {isFirstReview ? vsParStr : dirtyList.length}
                      </div>
                    </div>
                    <div className="ge-stat">
                      <div className="ge-stat-label">Course handicap</div>
                      <div className="ge-stat-value">{courseHandicap || '—'}</div>
                    </div>
                  </div>

                  <div className="bg-surface border border-line rounded-2xl overflow-hidden divide-y divide-line">
                    {(isFirstReview ? holeNumbers : (dirtyList.length > 0 ? dirtyList : holeNumbers)).map((holeNum) => {
                      const idx = scores.findIndex(s => s.hole === holeNum);
                      const cur = idx >= 0 ? scores[idx].strokes : 0;
                      const par = getPar(holeNum);
                      const best = bestScores?.hole_scores?.find(h => h.hole === holeNum)?.strokes || 0;
                      const isDirty = dirtyHoles.has(holeNum);
                      const d = par && cur > 0 ? cur - par : null;
                      const tone = isDirty ? 'text-accent'
                        : d === null ? (cur > 0 ? 'text-ink' : 'text-ink-soft')
                        : d < 0 ? 'text-accent' : d > 0 ? 'text-ink-soft' : 'text-ink';

                      return (
                        <button
                          key={holeNum}
                          type="button"
                          data-testid={`review-hole-${holeNum}`}
                          onClick={() => {
                            setReviewing(false);
                            if (isFirstRound) setActiveHoleIdx(holeNum - start);
                            else setEditingHole(holeNum);
                          }}
                          className="w-full grid grid-cols-[40px_72px_1fr_auto] items-center gap-3 px-4 sm:px-5 py-3 hover:bg-app/60 transition-colors text-left"
                        >
                          <div className="font-mono tabular-nums text-ink-muted text-sm font-medium">{holeNum.toString().padStart(2, '0')}</div>
                          {par ? (
                            <div className="font-mono tabular-nums text-[11px] uppercase tracking-[0.08em] text-ink-soft text-left whitespace-nowrap">
                              Par {par}
                            </div>
                          ) : <div />}
                          <div className="flex items-baseline gap-2">
                            <span className={`font-mono tabular-nums text-3xl font-semibold leading-none ${tone}`}>{cur || '—'}</span>
                            {!isFirstReview && best > 0 && cur > 0 && cur < best && (
                              <span className="text-[12px] text-ink-muted">was <b className="font-mono">{best}</b></span>
                            )}
                            {d !== null && cur > 0 && (
                              <span className={`text-[11.5px] font-mono font-medium ${tone}`}>{d > 0 ? `+${d}` : (d === 0 ? 'E' : d)}</span>
                            )}
                          </div>
                          <div className="text-[12px] text-ink-soft">Tap to edit</div>
                        </button>
                      );
                    })}
                  </div>

                  <div className="grid grid-cols-2 gap-3 mt-5">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => setReviewing(false)}
                      data-testid="review-back-btn"
                    >
                      <ArrowLeft className="w-4 h-4 mr-1.5" />
                      Back
                    </Button>
                    <Button
                      type="submit"
                      data-testid="submit-scores-button"
                      className="bg-ink hover:bg-ink-hover text-white font-semibold"
                      disabled={!detailsReady || submitting}
                    >
                      <Save className="w-4 h-4 mr-1.5" />
                      {submitting ? "Submitting…" : (isFirstReview ? `Submit round · ${roundTotal}` : (dirtyList.length > 0 ? `Save round · ${dirtyList.length} improved` : 'Save round'))}
                    </Button>
                  </div>
                </div>
              );
            }

            // ── ROUND 1 CAROUSEL ──
            if (isFirstRound) {
              return (
                <div data-testid="round1-carousel">
                  {!detailsReady && (
                  <div className="mb-4 px-4 py-3 bg-app border border-line rounded-xl flex items-start gap-3">
                    <div className="w-7 h-7 rounded-full bg-ink text-white flex items-center justify-center flex-shrink-0 mt-0.5">
                      <span className="font-mono text-sm font-semibold">!</span>
                    </div>
                    <div>
                      <p className="text-[13.5px] font-semibold text-ink">Round details required</p>
                      <p className="text-[12.5px] text-ink-muted mt-0.5">Enter the date played and your course handicap above before recording scores.</p>
                    </div>
                  </div>
                )}

                  {/* Top stat band */}
                  <div className="ge-stat-grid mb-5">
                    <div className="ge-stat">
                      <div className="ge-stat-label">Round total</div>
                      <div className="ge-stat-value">{roundTotal || 0}</div>
                    </div>
                    <div className="ge-stat">
                      <div className="ge-stat-label">Entered</div>
                      <div className="ge-stat-value">{enteredCount}<span className="text-ink-soft text-base font-mono ml-1">/{total}</span></div>
                    </div>
                    <div className="ge-stat">
                      <div className="ge-stat-label">Through</div>
                      <div className="ge-stat-value">{r1ActiveIdx + 1}<span className="text-ink-soft text-base font-mono ml-1">/{total}</span></div>
                    </div>
                    <div className="ge-stat">
                      <div className="ge-stat-label">Vs par</div>
                      <div className={`ge-stat-value ${vsParTone}`}>{vsParStr}</div>
                    </div>
                  </div>

                  {/* Active hole card */}
                  <div className="bg-surface border border-line rounded-2xl p-6 sm:p-8 mb-5">
                    <div className="flex items-start justify-between mb-6">
                      <div>
                        <div className="text-[11px] font-semibold tracking-[0.08em] uppercase text-ink-muted">Hole</div>
                        <div className="font-mono tabular-nums text-5xl sm:text-6xl font-semibold tracking-tight text-ink leading-none mt-1.5">
                          {r1ActiveHole.toString().padStart(2, '0')}
                        </div>
                      </div>
                      {r1Par && (
                        <div className="inline-flex items-center gap-1.5 font-mono tabular-nums text-[12px] font-semibold uppercase tracking-[0.08em] px-2.5 py-1.5 rounded-lg bg-accent-tint text-accent-deep border border-accent/20">
                          <Trophy className="w-3 h-3" />
                          <span>Par {r1Par}</span>
                        </div>
                      )}
                    </div>

                    <div className="text-center my-6 sm:my-8">
                      <div className="text-[11px] font-semibold tracking-[0.08em] uppercase text-ink-muted mb-2">Your score</div>
                      <div className={`font-mono tabular-nums text-[88px] sm:text-[120px] font-semibold tracking-tight leading-none ${r1Tone}`}>
                        {r1Score === 0 ? '—' : r1Score}
                      </div>
                      <div className={`mt-2 text-sm font-medium ${r1Score === 0 ? 'text-ink-soft' : r1Tone}`}>{r1DiffLabel}</div>
                    </div>

                    <div className="flex items-center justify-center gap-6 mb-6">
                      <button
                        type="button"
                        onClick={() => setStrokes(r1ActiveHole, r1Score - 1)}
                        disabled={r1Score <= 0 || !detailsReady}
                        data-testid={`r1-hole-${r1ActiveHole}-minus`}
                        className="w-14 h-14 rounded-full border border-line bg-surface text-ink hover:bg-app hover:border-line-strong active:scale-95 transition-all flex items-center justify-center disabled:opacity-30"
                      >
                        <Minus className="w-6 h-6" />
                      </button>
                      <button
                        type="button"
                        onClick={() => setStrokes(r1ActiveHole, r1Score + 1)}
                        disabled={r1Score >= 10 || !detailsReady}
                        data-testid={`r1-hole-${r1ActiveHole}-plus`}
                        className="w-14 h-14 rounded-full bg-ink text-white hover:bg-ink-hover active:scale-95 transition-all flex items-center justify-center disabled:opacity-30"
                      >
                        <Plus className="w-6 h-6" />
                      </button>
                    </div>

                    <div className="grid grid-cols-2 gap-3">
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => setActiveHoleIdx(Math.max(0, r1ActiveIdx - 1))}
                        disabled={r1ActiveIdx === 0}
                        data-testid="r1-prev-btn"
                      >
                        <ArrowLeft className="w-4 h-4 mr-1.5" />
                        Previous
                      </Button>
                      <Button
                        type="button"
                        onClick={() => {
                          if (r1ActiveIdx === holeNumbers.length - 1 && enteredCount === total) {
                            setReviewing(true);
                          } else {
                            setActiveHoleIdx(Math.min(holeNumbers.length - 1, r1ActiveIdx + 1));
                          }
                        }}
                        disabled={r1Score === 0 || (r1ActiveIdx === holeNumbers.length - 1 && enteredCount < total)}
                        className="bg-ink hover:bg-ink-hover text-white"
                        data-testid="r1-next-btn"
                      >
                        {r1ActiveIdx === holeNumbers.length - 1 ? 'Review & submit' : 'Next hole'}
                      </Button>
                    </div>
                  </div>

                  {/* Mini scorecard strip */}
                  <div className="bg-surface border border-line rounded-xl p-3 mb-5">
                    <div className="text-[10.5px] font-semibold tracking-[0.06em] uppercase text-ink-muted mb-2 px-1">All holes — tap to jump</div>
                    <div className={`grid gap-1.5 ${total === 18 ? 'grid-cols-9' : 'grid-cols-9'}`}>
                      {holeNumbers.map((hn, i) => {
                        const s = scores[i]?.strokes || 0;
                        const par = getPar(hn);
                        const isActive = i === r1ActiveIdx;
                        const empty = s === 0;
                        const d = par && !empty ? s - par : null;
                        const tone = empty ? 'text-ink-soft bg-app'
                          : d === null ? 'text-ink bg-surface'
                          : d < 0 ? 'text-accent bg-accent-tint'
                          : d > 0 ? 'text-ink-soft bg-app'
                          : 'text-ink bg-surface';
                        return (
                          <button
                            key={hn}
                            type="button"
                            onClick={() => setActiveHoleIdx(i)}
                            data-testid={`r1-jump-${hn}`}
                            className={`relative rounded-md py-2 text-center transition-all border ${
                              isActive ? 'border-ink ring-2 ring-ink/20' : 'border-line hover:border-line-strong'
                            } ${tone}`}
                          >
                            <div className="font-mono tabular-nums text-[9.5px] text-ink-muted leading-none">{hn}</div>
                            <div className="font-mono tabular-nums text-[15px] font-semibold leading-none mt-1">{empty ? '—' : s}</div>
                          </button>
                        );
                      })}
                    </div>
                  </div>

                  <Button
                    type="button"
                    onClick={() => setReviewing(true)}
                    disabled={enteredCount < total || !detailsReady}
                    className="w-full bg-ink hover:bg-ink-hover text-white py-6 text-base font-semibold"
                    data-testid="r1-review-cta"
                  >
                    <Save className="w-5 h-5 mr-1.5" />
                    {enteredCount === total ? `Review & submit · ${roundTotal}` : `${total - enteredCount} hole${total - enteredCount === 1 ? '' : 's'} left`}
                  </Button>
                </div>
              );
            }

            // ── SUBSEQUENT ROUND: tap-to-improve list (Variant B styling) ──
            const improvedCount = dirtyHoles.size;
            const strokesSaved = Array.from(dirtyHoles).reduce((acc, holeNum) => {
              const idx = scores.findIndex(s => s.hole === holeNum);
              const best = bestScores?.hole_scores?.find(h => h.hole === holeNum)?.strokes || 0;
              const cur = idx >= 0 ? scores[idx].strokes : best;
              return acc + Math.max(0, best - cur);
            }, 0);
            const startingNet = bestScores?.total_score
              ? bestScores.total_score - Math.round((parseInt(courseHandicap) || 0) / 2)
              : null;
            const newNet = startingNet !== null ? startingNet - strokesSaved : null;

            return (
              <div data-testid="subsequent-improve">
                {!detailsReady && (
                  <div className="mb-4 px-4 py-3 bg-app border border-line rounded-xl flex items-start gap-3">
                    <div className="w-7 h-7 rounded-full bg-ink text-white flex items-center justify-center flex-shrink-0 mt-0.5">
                      <span className="font-mono text-sm font-semibold">!</span>
                    </div>
                    <div>
                      <p className="text-[13.5px] font-semibold text-ink">Round details required</p>
                      <p className="text-[12.5px] text-ink-muted mt-0.5">Enter the date played and your course handicap above before recording scores.</p>
                    </div>
                  </div>
                )}

                {/* Live summary */}
                <div className="bg-app border border-line-strong rounded-2xl mb-5 p-5 ring-1 ring-line shadow-sm" data-testid="live-summary">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <div className="text-[10.5px] tracking-[0.06em] uppercase text-ink-muted font-semibold">Eclectic Net</div>
                      <div className="font-mono text-3xl font-semibold tracking-tight tabular-nums leading-none mt-1.5 text-ink">
                        {newNet !== null ? newNet : '—'}
                      </div>
                      <div className="text-[12px] text-ink-muted mt-1.5">
                        {bestScores?.total_score
                          ? `Gross ${bestScores.total_score - strokesSaved} · h/cap ${courseHandicap || '—'}`
                          : 'Awaiting first improvement'}
                      </div>
                    </div>
                    <div className="text-right border-l border-line pl-4 sm:pl-6">
                      <div className="text-[10.5px] tracking-[0.06em] uppercase text-ink-muted font-semibold">This round</div>
                      <div className="font-mono text-3xl font-semibold tracking-tight tabular-nums leading-none mt-1.5 text-accent" data-testid="improved-count">
                        {improvedCount}
                        <span className="text-base text-ink-muted font-medium ml-1.5">improved</span>
                      </div>
                      <div className="text-[12px] text-ink-muted mt-1.5" data-testid="strokes-saved">
                        {strokesSaved} stroke{strokesSaved === 1 ? '' : 's'} saved
                      </div>
                    </div>
                  </div>
                </div>

                <div className="mb-4">
                  <div className="text-[11px] font-semibold tracking-[0.08em] uppercase text-ink-muted mb-1">Improve your best</div>
                  <p className="text-[13px] text-ink-muted">Tap a hole where you beat your current best. Untouched holes stay at their locked best.</p>
                </div>

                {/* Holes list */}
                <div className="bg-surface border border-line rounded-2xl overflow-hidden divide-y divide-line mb-5" data-testid="holes-list">
                  {holeNumbers.map((holeNum) => {
                    const idx = scores.findIndex(s => s.hole === holeNum);
                    const cur = idx >= 0 ? scores[idx].strokes : 0;
                    const best = bestScores?.hole_scores?.find(h => h.hole === holeNum)?.strokes || 0;
                    const par = getPar(holeNum);
                    const isDirty = dirtyHoles.has(holeNum);
                    const isEditing = editingHole === holeNum;
                    const showImprove = cur < best;
                    const isHoleInOne = isDirty && cur === 1;
                    const isExcellent2 = isDirty && cur === 2 && competition?.recognize_score_of_2;

                    if (isEditing) {
                      const stepperTone = showImprove ? 'text-accent' : 'text-ink';
                      return (
                        <div
                          key={holeNum}
                          data-testid={`hole-row-${holeNum}`}
                          className="bg-surface ring-2 ring-accent ring-inset"
                        >
                          <div className="px-4 sm:px-5 py-5">
                            <div className="flex items-start justify-between gap-3 mb-4">
                              <div className="flex items-center gap-2.5">
                                <div className="font-mono tabular-nums text-ink-muted text-sm font-medium">
                                  {holeNum.toString().padStart(2, '0')}
                                </div>
                                <div>
                                  <div className="text-[11px] font-semibold tracking-[0.08em] uppercase text-ink-muted">Hole {holeNum}</div>
                                  <div className="text-[12px] text-ink-muted mt-0.5">tap +/− to change your score</div>
                                </div>
                              </div>
                              <div className="flex items-center gap-2">
                                {par && (
                                  <div className="inline-flex items-center font-mono tabular-nums text-[11px] font-semibold uppercase tracking-[0.08em] px-2 py-1 rounded-md bg-accent-tint text-accent-deep border border-accent/20">
                                    Par {par}
                                  </div>
                                )}
                                <div className="text-right">
                                  <div className="text-[10px] font-semibold tracking-[0.06em] uppercase text-ink-muted">Best</div>
                                  <div className="font-mono tabular-nums text-xl font-semibold text-ink leading-none mt-0.5">{best || '—'}</div>
                                </div>
                              </div>
                            </div>

                            <div className="flex items-center justify-center gap-5 py-4">
                              <button
                                type="button"
                                onClick={() => setStrokes(holeNum, Math.max(1, cur - 1))}
                                disabled={cur <= 1}
                                className="w-12 h-12 rounded-full border border-line bg-surface text-ink hover:bg-app hover:border-line-strong disabled:opacity-30 active:scale-95 transition-all flex items-center justify-center"
                                data-testid={`hole-${holeNum}-minus`}
                                aria-label="Decrease"
                              >
                                <Minus className="w-5 h-5" />
                              </button>
                              <div className={`font-mono tabular-nums text-6xl font-semibold tracking-tight min-w-[80px] text-center leading-none transition-colors ${stepperTone}`}>
                                {cur}
                              </div>
                              <button
                                type="button"
                                onClick={() => setStrokes(holeNum, Math.min(10, cur + 1))}
                                disabled={cur >= 10}
                                className="w-12 h-12 rounded-full bg-ink text-white hover:bg-ink-hover disabled:opacity-30 active:scale-95 transition-all flex items-center justify-center"
                                data-testid={`hole-${holeNum}-plus`}
                                aria-label="Increase"
                              >
                                <Plus className="w-5 h-5" />
                              </button>
                            </div>

                            <div className={`px-3 py-2 rounded-md text-center text-[13px] font-medium mb-4 transition-colors ${
                              showImprove ? 'bg-accent-tint text-accent-deep' : 'bg-app text-ink-muted'
                            }`}>
                              {showImprove
                                ? `▼ ${best - cur} stroke${best - cur === 1 ? '' : 's'} better than your best`
                                : (cur === best ? 'Matches your best — no change' : `${cur - best} over your best — won't be saved`)}
                            </div>

                            <div className="grid grid-cols-2 gap-2">
                              <Button
                                type="button"
                                variant="outline"
                                onClick={() => {
                                  if (!dirtyHoles.has(holeNum)) {
                                    setStrokes(holeNum, best);
                                  }
                                  setEditingHole(null);
                                }}
                                data-testid={`hole-${holeNum}-cancel`}
                              >
                                Cancel
                              </Button>
                              <Button
                                type="button"
                                onClick={() => {
                                  if (!showImprove) return;
                                  const newDirty = new Set(dirtyHoles);
                                  newDirty.add(holeNum);
                                  setDirtyHoles(newDirty);
                                  setEditingHole(null);
                                }}
                                disabled={!showImprove}
                                className="bg-accent hover:bg-accent-deep text-white border-accent disabled:opacity-40"
                                data-testid={`hole-${holeNum}-confirm`}
                              >
                                {showImprove ? 'Save new best' : 'No improvement'}
                              </Button>
                            </div>
                          </div>
                        </div>
                      );
                    }

                    // Collapsed row (Variant B-styled)
                    const rowDiff = par && cur > 0 ? cur - par : null;
                    const rowTone = isDirty ? 'text-accent'
                      : rowDiff === null ? 'text-ink'
                      : rowDiff < 0 ? 'text-accent'
                      : rowDiff > 0 ? 'text-ink-soft'
                      : 'text-ink';

                    return (
                      <div
                        key={holeNum}
                        data-testid={`hole-row-${holeNum}`}
                        onClick={() => detailsReady && setEditingHole(holeNum)}
                        className={`grid grid-cols-[40px_72px_1fr_auto] items-center gap-3 px-4 sm:px-5 py-3.5 transition-colors ${
                          isDirty ? 'bg-accent-tint-2' : 'bg-surface hover:bg-app'
                        } ${!detailsReady ? 'opacity-60 cursor-not-allowed' : 'cursor-pointer'}`}
                      >
                        <div className="font-mono tabular-nums text-sm font-medium text-ink-muted">
                          {holeNum.toString().padStart(2, '0')}
                        </div>
                        {par ? (
                          <div className="font-mono tabular-nums text-[11px] uppercase tracking-[0.08em] text-ink-soft text-left whitespace-nowrap">
                            Par {par}
                          </div>
                        ) : <div />}
                        <div className="flex items-baseline gap-2">
                          <span className={`font-mono tabular-nums text-3xl font-semibold leading-none ${rowTone}`}>{cur || '—'}</span>
                          {isDirty && best > 0 && cur > 0 && cur < best && (
                            <span className="text-[12px] text-ink-muted">
                              <span className="font-mono tabular-nums">{best}</span>
                              <span className="mx-1">→</span>
                              <span className="font-mono tabular-nums text-accent font-semibold">{cur}</span>
                            </span>
                          )}
                          {!isDirty && best > 0 && (
                            <span className="text-[12px] text-ink-muted">
                              best <b className="font-mono tabular-nums font-semibold">{best}</b>
                            </span>
                          )}
                          {isHoleInOne && <span className="ml-1 inline-block text-[10.5px] text-warm font-semibold uppercase tracking-wide">Hole-in-one</span>}
                          {isExcellent2 && <span className="ml-1 inline-block text-[10.5px] text-warm font-semibold uppercase tracking-wide">Excellent 2</span>}
                        </div>
                        <button
                          type="button"
                          onClick={(e) => { e.stopPropagation(); if (detailsReady) setEditingHole(holeNum); }}
                          disabled={!detailsReady}
                          data-testid={`hole-improve-${holeNum}-btn`}
                          className={`text-[12.5px] font-medium px-3 py-1.5 rounded-md border transition-colors ${
                            isDirty
                              ? 'bg-accent text-white border-accent hover:bg-accent-deep'
                              : 'bg-surface text-ink-muted border-line hover:border-line-strong hover:text-ink'
                          } disabled:opacity-40 disabled:cursor-not-allowed`}
                        >
                          {isDirty ? 'Edit' : 'Improve'}
                        </button>
                      </div>
                    );
                  })}
                </div>

                <Button
                  type="button"
                  onClick={() => setReviewing(true)}
                  disabled={!detailsReady}
                  className="w-full bg-ink hover:bg-ink-hover text-white py-6 text-base font-semibold"
                  data-testid="subsequent-review-cta"
                >
                  <Save className="w-5 h-5 mr-1.5" />
                  {dirtyHoles.size > 0 ? `Review & submit · ${dirtyHoles.size} improved` : 'Review & submit'}
                </Button>
              </div>
            );
          })()}
        </form>
      </main>

      {/* Error Dialog - Prominent and requires acknowledgment */}
      <AlertDialog open={errorDialog.open} onOpenChange={(open) => setErrorDialog({ ...errorDialog, open })}>
        <AlertDialogContent className="max-w-[90vw] sm:max-w-md mx-4 bg-white">
          <AlertDialogHeader className="space-y-4">
            <div className="flex flex-col items-center text-center gap-4">
              <div className="w-16 h-16 rounded-full bg-red-100 flex items-center justify-center flex-shrink-0">
                <AlertCircle className="w-8 h-8 text-red-600" />
              </div>
              <div className="w-full">
                <AlertDialogTitle className="text-xl sm:text-2xl font-bold !text-ink mb-4">
                  {errorDialog.title}
                </AlertDialogTitle>
                <AlertDialogDescription className="text-base sm:text-lg !text-ink leading-relaxed font-medium">
                  {errorDialog.message}
                </AlertDialogDescription>
              </div>
            </div>
          </AlertDialogHeader>
          <AlertDialogFooter className="mt-6">
            <AlertDialogAction 
              onClick={() => setErrorDialog({ open: false, title: "", message: "" })}
              className="w-full bg-ink hover:bg-ink-hover !text-white font-bold py-4 text-base sm:text-lg rounded-lg"
            >
              OK, I'll Fix That
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
